import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { signInWithGoogle, signInWithApple } from '../../lib/auth';
import { createPortal } from 'react-dom';
import { OrnamentDivider } from '../ui/OrnamentDivider';

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
}

export function LoginModal({ open, onClose }: LoginModalProps) {
  const { t } = useTranslation('common');

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-mafia-black/80 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="card-vintage w-full max-w-sm mx-4 p-8 shadow-crimson relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-mafia-muted hover:text-mafia-paper transition-colors text-sm font-mono"
              aria-label="Закрыть"
            >
              ✕
            </button>

            {/* Header */}
            <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase text-center mb-2">
              ◆ Войти ◆
            </p>
            <h2 className="font-display text-2xl font-bold text-mafia-paper text-center mb-1">
              {t('auth.title')}
            </h2>
            <p className="text-mafia-paper-dim text-sm text-center mb-6">{t('auth.subtitle')}</p>

            <OrnamentDivider className="mb-6" variant="dim" />

            <div className="space-y-3">
              <button
                onClick={() => signInWithGoogle()}
                className="w-full flex items-center justify-center gap-3 bg-white text-gray-900 font-medium py-3 px-4 hover:bg-gray-100 transition-colors text-sm"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t('auth.signInGoogle')}
              </button>

              <button
                onClick={() => signInWithApple()}
                className="w-full flex items-center justify-center gap-3 bg-mafia-black text-mafia-paper font-medium py-3 px-4 border border-mafia-border hover:border-mafia-paper-dim transition-colors text-sm"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                {t('auth.signInApple')}
              </button>
            </div>

            <p className="text-mafia-muted text-xs text-center mt-6 leading-relaxed">
              {t('auth.termsPrefix')}{' '}
              <Link to="/terms" onClick={onClose} className="text-mafia-paper-dim hover:text-mafia-gold transition-colors underline underline-offset-2">
                {t('auth.termsLink')}
              </Link>{' '}
              {t('auth.termsAnd')}{' '}
              <Link to="/privacy" onClick={onClose} className="text-mafia-paper-dim hover:text-mafia-gold transition-colors underline underline-offset-2">
                {t('auth.privacyLink')}
              </Link>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
